<template>
  <div
    class="relative h-[688px] w-full bg-cover bg-center"
    :class="{
      'bg-mobile': true,
      'md:bg-desktop': true
    }"
  >
    <div class="absolute inset-0 bg-[#f8f4e9] bg-opacity-0"></div>
    <div class="relative container mx-auto px-10 py-10">
      <div class="max-w-2xl text-white">
        <h1 class="text-3xl text-[#2f5c45] font-bold mb-2 whitespace-pre-line">
          {{ t('home.hero.title') }}
        </h1>
        <p class="text-sm text-[#000] mb-6">
          {{ t('home.hero.subtitle') }}
        </p>
        <div class="flex space-x-4">
          <a-button
            type="primary"
            class="!rounded-button bg-[#409e70] border-none text-white hover:bg-green-800"
          >
            {{ t('home.hero.button') }}
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup name="HeroSection">
  import { useI18n } from 'vue-i18n'
  import backgroundImg from '../../../assets/other/1925168219146432514.jpg'
  import backgroundImgMb from '../../../assets/mb/mmexport1750053681721.jpg'

  const { t } = useI18n()
</script>

<style scoped>
/* 移动端背景图片 */
.bg-mobile {
  background-image: url('../../../assets/mb/b594b171c24395fe2b6796a6e290b65.jpg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  /* width: 100vw; */
  /* min-width: auto; */
}

/* 桌面端背景图片 */
@media (min-width: 768px) {
  .md\:bg-desktop {
    background-image: url('../../../assets/other/1925168219146432514.jpg');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center center;
  }
}

/* 移动端优化 - 保持PC端原有样式 */
@media (max-width: 768px) {
  /* 移动端高度调整 */
  .relative.h-\[688px\] {
    height: 450px;
  }
  
  /* 移动端内边距调整 */
  .container.px-10.py-10 {
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }
  
  /* 移动端标题字体大小优化 */
  .text-3xl {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin-bottom: 0.75rem;
  }
  
  /* 移动端段落文字优化 */
  .text-sm {
    font-size: 0.8rem;
    line-height: 1.4;
    margin-bottom: 1.25rem;
  }
  
  /* 移动端按钮布局调整 - 靠左对齐，不占满宽度 */
  .flex.space-x-4 {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  /* 移动端按钮样式优化 - 适中大小，靠左对齐 */
  .ant-btn {
    width: auto;
    min-width: 120px;
    height: 36px;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 6px;
    padding: 0 1.5rem;
  }
  
  /* 移动端文字阴影增强可读性 */
  h1 {
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
  
  p {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
  
  /* 移动端背景图片优化 */
  .bg-cover {
    background-size: cover !important;
    background-position: center center !important;
  }
  
  /* 移动端最大宽度调整 */
  .max-w-2xl {
    max-width: 100%;
    padding: 0 0.5rem;
  }
}

/* 小屏手机适配 (320px-480px) */
@media (max-width: 480px) {
  .relative.h-\[688px\] {
    height: 400px;
  }
  
  .text-3xl {
    font-size: 1.125rem;
    line-height: 1.5rem;
  }
  
  .text-sm {
    font-size: 0.75rem;
    line-height: 1.3;
  }
  
  .ant-btn {
    height: 34px;
    font-size: 0.8rem;
    min-width: 110px;
    padding: 0 1.25rem;
  }
}

/* 平板端适配 */
@media (min-width: 769px) and (max-width: 1024px) {
  /* 平板端高度微调 */
  .relative.h-\[688px\] {
    height: 600px;
  }
  
  /* 平板端内边距微调 */
  .container.px-10.py-10 {
    padding-left: 2rem;
    padding-right: 2rem;
  }
  
  /* 平板端标题字体微调 */
  .text-3xl {
    font-size: 1.75rem;
    line-height: 2.25rem;
  }
  
  .text-sm {
    font-size: 0.9rem;
  }
}

/* PC端保持完全原有样式 - 1025px及以上 */
@media (min-width: 1025px) {
  /* PC端所有样式保持原有，不做任何修改 */
  /* 确保原有的布局、字体大小、间距完全不受影响 */
}

/* 通用移动端触摸优化 */
@media (hover: none) and (pointer: coarse) {
  .ant-btn {
    -webkit-tap-highlight-color: transparent;
    transition: transform 0.1s ease;
  }
  
  .ant-btn:active {
    transform: scale(0.98);
  }
}

/* 确保可访问性 */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* 横屏模式优化 */
@media (max-width: 768px) and (orientation: landscape) {
  .relative.h-\[688px\] {
    height: 350px;
  }
  
  .container.px-10.py-10 {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
}
</style>
